import { useEffect, useState } from "react";
import {
  NPC_CHAT_LOG_DAYS,
  chatLogDaysLeft,
  frequentQuestions,
  qaDraftFromLog,
  type NpcChatLogRow,
  type NpcQa,
} from "@greg-chick/shared";
import { deleteNpcLog, getNpcLog } from "../lib/contentApi";

/**
 * 🗒️ **들어온 질문(30일)** — 자유 대화로 들어온 질문을 읽고, 쓸 만한 것을 **승인 문답 초안**으로 옮긴다 (FR-NPC-05 · 2026-10-14).
 *
 * 남는 것은 이미 AI 에 보낸 **마스킹된** 질문·답뿐이고 누가 물었는지는 없다. {NPC_CHAT_LOG_DAYS}일 지나면 저절로 지워진다.
 * 영구히 남기고 싶은 질문은 여기서 [📋 승인 문답 초안으로] — 운영자가 다듬어 ✅ 한 **버튼 문답**만 남는다.
 * 🔴 옮기기는 이 화면의 문답 대기함에 넣을 뿐이다 — 아래 [웹에 올리기]를 눌러야 아이에게 나간다(묻지 않고 게시하지 않는다).
 */
export function NpcChatLogBox({
  npc,
  qa,
  onDraft,
}: {
  npc: string;
  qa: readonly NpcQa[];
  onDraft: (d: NpcQa) => void;
}) {
  const [rows, setRows] = useState<NpcChatLogRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setErr(null);
    getNpcLog(npc)
      .then((r) => alive && setRows(r))
      .catch((e: unknown) => alive && setErr(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [npc]);

  function toDraft(r: NpcChatLogRow) {
    const d = qaDraftFromLog(r, qa, Date.now());
    if (!d) {
      setMsg(`「${r.q.slice(0, 20)}」 — 이미 같은 질문이 문답에 있어요.`);
      return;
    }
    onDraft(d);
    setMsg(`📋 「${d.q.slice(0, 20)}」을(를) 문답 대기함에 넣었어요 — 답을 다듬어 ✅ 한 뒤 아래 [웹에 올리기]를 눌러야 아이에게 나가요.`);
  }

  async function remove(r: NpcChatLogRow) {
    if (!window.confirm(`이 질문을 지울까요? 되돌릴 수 없어요.\n\n「${r.q.slice(0, 60)}」`)) return;
    try {
      await deleteNpcLog(r.id);
      setRows((list) => (list ?? []).filter((x) => x.id !== r.id));
      setMsg("🗑 지웠어요.");
    } catch (e) {
      setMsg(`⚠️ ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  const now = Date.now();
  const top = rows ? frequentQuestions(rows, 5).filter((t) => t.n > 1) : [];

  return (
    <div className="agent__log" data-npc-log>
      <h4 className="agent__h">🗒️ 들어온 질문 — {NPC_CHAT_LOG_DAYS}일 뒤 저절로 지워져요</h4>
      <p className="hint">
        이름·전화번호 같은 글자는 지운 채 남아요. 누가 물었는지는 남지 않아요. 오래 남기고 싶은 질문은{" "}
        <b>[📋 승인 문답 초안으로]</b> — 다듬어 ✅ 한 버튼 문답만 계속 남아요.
      </p>
      {err ? <p className="hint" role="alert">⚠️ {err}</p> : null}
      {rows === null && !err ? <p className="hint">불러오는 중…</p> : null}
      {rows && rows.length === 0 ? <p className="hint">아직 들어온 질문이 없어요.</p> : null}
      {top.length ? (
        <p className="hint">
          자주 나온 질문: {top.map((t) => `「${t.q.slice(0, 18)}」 ${t.n}번`).join(" · ")}
        </p>
      ) : null}
      {msg ? <p className="hint" role="status">{msg}</p> : null}
      {rows && rows.length ? (
        <ul className="agent__loglist">
          {rows.slice(0, 100).map((r) => (
            <li key={r.id} className="agent__logrow">
              <div>
                <b>Q.</b> {r.q}
                <br />
                <small>A. {r.a.slice(0, 120)}{r.a.length > 120 ? "…" : ""}</small>
                <br />
                <small className="agent__logmeta">
                  {r.at.slice(0, 10)} · {r.provider} · {chatLogDaysLeft(r.at, now)}일 뒤 지워짐
                </small>
              </div>
              <div className="agent__logbtns">
                <button type="button" className="btn btn--sm" onClick={() => toDraft(r)}>
                  📋 승인 문답 초안으로
                </button>
                <button type="button" className="btn btn--sm" onClick={() => void remove(r)}>
                  🗑 지우기
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
